import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  Paper,
  IconButton,
  Breadcrumbs,
  Link as MuiLink,
  CircularProgress,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack,
  Share,
  Favorite,
  Star,
  CalendarToday,
  Business,
  LocationOn,
  AttachMoney,
  Timeline,
  CheckCircle,
  Engineering,
  TrendingUp,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface ProjectDetailProps {}

export const ProjectDetail: React.FC<ProjectDetailProps> = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) {
      fetchProject();
    }
  }, [projectId]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API call
      // const response = await portfolioApi.getProjectById(projectId!);
      
      // Mock data for now
      const mockProject = {
        id: projectId,
        title: 'Advanced Manufacturing Facility',
        category: 'Manufacturing',
        client: 'TechCorp Industries',
        location: 'Nairobi, Kenya',
        duration: '18 months',
        budget: '$2.5M',
        description: 'A state-of-the-art manufacturing facility designed for high-volume production with advanced automation and quality control systems.',
        longDescription: `This comprehensive manufacturing facility represents a significant milestone in industrial automation and sustainable production. The project involved the design and construction of a 50,000 square foot facility equipped with cutting-edge manufacturing equipment, automated assembly lines, and integrated quality control systems.

        The facility features advanced robotics, IoT-enabled monitoring systems, and sustainable energy solutions including solar panels and energy-efficient HVAC systems. The project was completed on time and under budget, delivering exceptional value to our client while maintaining the highest standards of quality and safety.`,
        technologies: ['Automation', 'IoT', 'Robotics', 'Quality Control', 'Energy Management'],
        challenges: [
          'Complex automation integration',
          'Strict quality control requirements',
          'Tight timeline constraints',
          'Energy efficiency optimization'
        ],
        solutions: [
          'Modular automation design',
          'Advanced QC protocols',
          'Agile project management',
          'Sustainable energy systems'
        ],
        results: [
          '30% increase in production efficiency',
          '99.9% quality control accuracy',
          '25% reduction in energy costs',
          'Zero safety incidents'
        ],
        image: '/api/uploads/projects/project-1.jpg',
        gallery: [
          '/api/uploads/projects/project-1-1.jpg',
          '/api/uploads/projects/project-1-2.jpg',
          '/api/uploads/projects/project-1-3.jpg',
        ],
        team: [
          { name: 'John Doe', role: 'Project Manager', avatar: '/api/uploads/team/john.jpg' },
          { name: 'Jane Smith', role: 'Lead Engineer', avatar: '/api/uploads/team/jane.jpg' },
          { name: 'Mike Johnson', role: 'Automation Specialist', avatar: '/api/uploads/team/mike.jpg' },
        ],
        completionDate: '2023-12-15',
        status: 'Completed',
      };
      
      setProject(mockProject);
    } catch (err) {
      setError('Failed to load project details');
      console.error('Error fetching project:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Project not found'}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={handleBack}>
          Back to Portfolio
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3, color: 'white' }}>
          <MuiLink color="inherit" href="/" underline="hover">
            Home
          </MuiLink>
          <MuiLink color="inherit" href="/portfolio" underline="hover">
            Portfolio
          </MuiLink>
          <Typography color="inherit">{project.title}</Typography>
        </Breadcrumbs>

        {/* Project Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Grid container spacing={4}>
            {/* Project Image */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 3,
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                  minHeight: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {project.image ? (
                  <img
                    src={project.image}
                    alt={project.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Typography variant="h6" color="text.secondary">
                    Project Image
                  </Typography>
                )}
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    background: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': { background: 'rgba(255, 255, 255, 1)' },
                  }}
                >
                  <Favorite />
                </IconButton>
              </Box>
            </Grid>

            {/* Project Info */}
            <Grid item xs={12} md={6}>
              <Chip
                label={project.category}
                color="primary"
                sx={{ mb: 2 }}
              />
              
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
                {project.title}
              </Typography>

              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                {project.description}
              </Typography>

              {/* Project Stats */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Client</Typography>
                      <Typography variant="body1" fontWeight={500}>{project.client}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Location</Typography>
                      <Typography variant="body1" fontWeight={500}>{project.location}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Timeline sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Duration</Typography>
                      <Typography variant="body1" fontWeight={500}>{project.duration}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoney sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Budget</Typography>
                      <Typography variant="body1" fontWeight={500}>{project.budget}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Engineering />}
                  onClick={() => navigate('/contact')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
                    },
                  }}
                >
                  Start Similar Project
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Share />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    border: '2px solid #6366f1',
                    color: '#6366f1',
                    '&:hover': {
                      border: '2px solid #4f46e5',
                      background: 'rgba(99, 102, 241, 0.04)',
                    },
                  }}
                >
                  Share
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Project Details */}
        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            {/* Long Description */}
            <Paper
              elevation={0}
              sx={{
                p: 4,
                mb: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Project Overview
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                {project.longDescription}
              </Typography>
            </Paper>

            {/* Technologies Used */}
            <Paper
              elevation={0}
              sx={{
                p: 4,
                mb: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Technologies & Solutions
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Technologies Used
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {project.technologies?.map((tech: string) => (
                      <Chip
                        key={tech}
                        label={tech}
                        variant="outlined"
                        sx={{
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          mb: 1,
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Key Challenges
                  </Typography>
                  <List dense>
                    {project.challenges?.map((challenge: string, index: number) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CheckCircle sx={{ color: 'primary.main', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText primary={challenge} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </Paper>

            {/* Results & Impact */}
            <Paper
              elevation={0}
              sx={{
                p: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Results & Impact
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={2}>
                {project.results?.map((result: string, index: number) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, background: 'rgba(99, 102, 241, 0.05)' }}>
                      <TrendingUp sx={{ color: 'primary.main' }} />
                      <Typography variant="body1">{result}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Project Team */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Project Team
              </Typography>
              <Stack spacing={2}>
                {project.team?.map((member: any, index: number) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar src={member.avatar} sx={{ width: 40, height: 40 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {member.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {member.role}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>

            {/* Project Status */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Project Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <CheckCircle sx={{ color: 'success.main' }} />
                <Typography variant="body1" fontWeight={500}>
                  {project.status}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Completed on {new Date(project.completionDate).toLocaleDateString()}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}; 